import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { calendar, moduleColors } from '../data/studyData';
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameDay,
  addWeeks,
  subWeeks
} from 'date-fns';
import { ChevronLeft, ChevronRight, Clock, Video, Ship } from 'lucide-react';

export const WeeklyView = ({ 
  selectedDate, 
  onDateSelect, 
  currentWeek, 
  onWeekChange, 
  completedTasks,
  onToggleTask 
}) => {
  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  }, [currentWeek]);

  const getDayData = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return calendar.find(d => d.date === dateStr);
  };

  const isToday = (date) => isSameDay(date, new Date());

  return (
    <Card 
      className="rounded-2xl border-2 shadow-sm"
      data-testid="weekly-view"
    >
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-2xl">
            Week of {format(weekDays[0], 'MMMM d')}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => onWeekChange(subWeeks(currentWeek, 1))}
              data-testid="prev-week-btn"
              className="rounded-xl border-2"
              aria-label="Previous week"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              onClick={() => onWeekChange(new Date())}
              data-testid="this-week-btn"
              className="rounded-xl border-2 px-4"
            >
              This Week
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => onWeekChange(addWeeks(currentWeek, 1))}
              data-testid="next-week-btn"
              className="rounded-xl border-2"
              aria-label="Next week"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-3">
          {weekDays.map((date, index) => {
            const dayData = getDayData(date);
            const isSelected = isSameDay(date, selectedDate);
            const isTodayDate = isToday(date);
            const dateStr = format(date, 'yyyy-MM-dd');
            const completedForDay = completedTasks[dateStr] || [];
            const moduleStyle = dayData ? (moduleColors[dayData.module] || moduleColors.Rest) : null;

            return (
              <div
                key={index}
                className={`
                  week-day-column flex flex-col rounded-xl border-2 overflow-hidden
                  ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
                  ${isTodayDate ? 'is-today border-primary' : 'border-border'}
                  ${dayData?.isRetreat ? 'retreat-day' : ''}
                `}
                data-testid={`week-day-${format(date, 'yyyy-MM-dd')}`}
              >
                {/* Day Header */}
                <button
                  onClick={() => onDateSelect(date)}
                  className={`
                    p-3 text-center cursor-pointer transition-colors
                    ${dayData ? moduleStyle.bg : 'bg-muted/30'}
                    hover:opacity-80
                  `}
                  aria-label={`Select ${format(date, 'EEEE, MMMM d')}`}
                >
                  <p className="text-xs font-medium text-muted-foreground uppercase">
                    {format(date, 'EEE')}
                  </p>
                  <p className={`
                    text-2xl font-heading font-bold mt-1
                    ${isTodayDate ? 'text-primary' : dayData ? moduleStyle.text : 'text-foreground'}
                  `}>
                    {format(date, 'd')}
                  </p>
                  {dayData && (
                    <Badge 
                      className={`mt-2 ${moduleStyle.badgeBg} ${moduleStyle.badgeText} text-xs`}
                    >
                      {dayData.module}
                    </Badge>
                  )}
                </button>

                {/* Day Content */}
                <ScrollArea className="flex-1 max-h-[280px]">
                  <div className="p-3 space-y-3">
                    {dayData ? (
                      <>
                        {/* Retreat Badge */}
                        {dayData.isRetreat && (
                          <div className="flex items-center gap-1.5 p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30">
                            <Ship className="w-3 h-3 text-violet-600 dark:text-violet-400" />
                            <span className="text-xs font-medium text-violet-700 dark:text-violet-300">
                              Retreat
                            </span>
                          </div>
                        )}

                        {/* Study Hours */}
                        {dayData.study_hours > 0 && (
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-sky-600" />
                            <span className="font-medium">{dayData.study_hours}h</span>
                          </div>
                        )}

                        {/* Topic */}
                        <p className={`text-xs font-medium ${moduleStyle.text} line-clamp-2`}>
                          {dayData.topic}
                        </p>

                        {/* Tasks */}
                        {dayData.tasks.length > 0 && (
                          <div className="space-y-1.5">
                            {dayData.tasks.slice(0, 3).map((task, taskIndex) => {
                              const isCompleted = completedForDay.includes(taskIndex);
                              return (
                                <div 
                                  key={taskIndex}
                                  className="flex items-start gap-2 cursor-pointer group"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleTask(dateStr, taskIndex);
                                  }}
                                >
                                  <Checkbox 
                                    checked={isCompleted}
                                    className="mt-0.5 h-4 w-4"
                                  />
                                  <span className={`
                                    text-xs leading-relaxed
                                    ${isCompleted ? 'line-through text-muted-foreground' : 'text-foreground'}
                                  `}>
                                    {task.length > 40 ? task.slice(0, 40) + '...' : task}
                                  </span>
                                </div>
                              );
                            })}
                            {dayData.tasks.length > 3 && (
                              <p className="text-xs text-muted-foreground">
                                +{dayData.tasks.length - 3} more tasks
                              </p>
                            )}
                          </div>
                        )}

                        {/* Tutorials */}
                        {dayData.tutorials.length > 0 && (
                          <div className="flex items-center gap-1.5 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                            <Video className="w-3 h-3 text-amber-600" />
                            <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                              {dayData.tutorials.length} tutorial{dayData.tutorials.length > 1 ? 's' : ''}
                            </span>
                          </div>
                        )}

                        {/* Rest Day */}
                        {dayData.module === 'Rest' && (
                          <div className="flex flex-col items-center justify-center py-4 text-center">
                            <span className="text-2xl mb-1">😴</span>
                            <p className="text-xs text-muted-foreground">Rest Day</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-4 text-center">
                        <p className="text-xs text-muted-foreground">No data</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
